/**
 * Country-aware FAQ overrides — merged into base faqData by stable entry id.
 * Keep EU payment copy in sync with countryProfiles.js / CountryPaymentProfiles.java.
 */

import { EU_PAYMENT_COUNTRY_CODES } from './countryProfiles';
import { faqText } from './faq/faqItem';

function isEu(countryCode) {
  const cc = String(countryCode || '').toUpperCase();
  return EU_PAYMENT_COUNTRY_CODES.has(cc);
}

function taxInvoiceAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  if (cc === 'IN') {
    return {
      en: 'No. ChessDesk provides payment receipts and session statements for academy records. It does not generate GST-compliant tax invoices (CGST/SGST breakdown, GSTIN, HSN/SAC, or GSTR exports). Consult your accountant for statutory invoicing if required.',
      de: 'Nein. ChessDesk liefert Zahlungsbelege und Sitzungsabrechnungen für Akademie-Aufzeichnungen. Es erstellt keine GST-konformen Steuerrechnungen (CGST/SGST-Aufschlüsselung, GSTIN, HSN/SAC oder GSTR-Exporte). Konsultieren Sie Ihren Steuerberater für gesetzliche Rechnungen.',
      fr: "Non. ChessDesk fournit reçus de paiement et relevés de séances pour les archives de l'académie. Il ne génère pas de factures fiscales GST conformes (détail CGST/SGST, GSTIN, HSN/SAC ou exports GSTR). Consultez votre comptable pour la facturation légale.",
      nl: 'Nee. ChessDesk levert betalingsbewijzen en sessieoverzichten voor academieregistratie. Het genereert geen GST-conforme belastingfacturen (CGST/SGST-uitsplitsing, GSTIN, HSN/SAC of GSTR-exports). Raadpleeg uw accountant voor wettelijke facturatie indien nodig.',
    };
  }
  if (cc === 'GB' || cc === 'AE' || isEu(cc)) {
    return {
      en: 'No. ChessDesk provides payment receipts and session statements for academy records. It does not generate statutory VAT tax invoices with full compliance breakdowns. Consult your accountant for official VAT invoicing if required.',
      de: 'Nein. ChessDesk liefert Zahlungsbelege und Sitzungsabrechnungen für Akademie-Aufzeichnungen. Es erstellt keine gesetzlichen MwSt.-Steuerrechnungen mit vollständigen Compliance-Aufschlüsselungen. Konsultieren Sie Ihren Steuerberater für offizielle MwSt.-Rechnungen.',
      fr: "Non. ChessDesk fournit reçus de paiement et relevés de séances pour les archives de l'académie. Il ne génère pas de factures fiscales TVA légales avec décomptes de conformité complets. Consultez votre comptable pour la facturation TVA officielle.",
      nl: 'Nee. ChessDesk levert betalingsbewijzen en sessieoverzichten voor academieregistratie. Het genereert geen wettelijke btw-belastingfacturen met volledige compliance-uitsplitsingen. Raadpleeg uw accountant voor officiële btw-facturatie indien nodig.',
    };
  }
  if (cc === 'US') {
    return {
      en: 'No. ChessDesk provides payment receipts and session statements for academy records. It does not generate sales-tax invoices or Form 1099 reporting. Consult your accountant for official tax documentation if required.',
      de: 'Nein. ChessDesk liefert Zahlungsbelege und Sitzungsabrechnungen für Akademie-Aufzeichnungen. Es erstellt keine Umsatzsteuerrechnungen oder Form-1099-Berichte. Konsultieren Sie Ihren Steuerberater für offizielle Steuerdokumente.',
      fr: "Non. ChessDesk fournit reçus de paiement et relevés de séances pour les archives de l'académie. Il ne génère pas de factures de taxe de vente ni de déclarations Form 1099. Consultez votre comptable pour la documentation fiscale officielle.",
      nl: 'Nee. ChessDesk levert betalingsbewijzen en sessieoverzichten voor academieregistratie. Het genereert geen sales-tax-facturen of Form 1099-rapportage. Raadpleeg uw accountant voor officiële belastingdocumentatie indien nodig.',
    };
  }
  return {
    en: 'No. ChessDesk provides payment receipts and session statements for academy records. It is not statutory tax-invoicing software — consult your accountant for official tax documents if required.',
    de: 'Nein. ChessDesk liefert Zahlungsbelege und Sitzungsabrechnungen für Akademie-Aufzeichnungen. Es ist keine gesetzliche Steuerrechnungssoftware — konsultieren Sie Ihren Steuerberater für offizielle Steuerdokumente.',
    fr: "Non. ChessDesk fournit reçus de paiement et relevés de séances pour les archives de l'académie. Ce n'est pas un logiciel de facturation fiscale légale — consultez votre comptable pour les documents fiscaux officiels.",
    nl: 'Nee. ChessDesk levert betalingsbewijzen en sessieoverzichten voor academieregistratie. Het is geen software voor wettelijke belastingfacturatie — raadpleeg uw accountant voor officiële belastingdocumenten indien nodig.',
  };
}

function walletBillingAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  const methods = {
    IN: {
      en: 'cash, UPI, bank transfer, or your configured online gateway',
      de: 'Bar, UPI, Banküberweisung oder Ihr konfiguriertes Online-Gateway',
      fr: 'espèces, UPI, virement bancaire ou votre passerelle en ligne configurée',
      nl: 'contant, UPI, bankoverschrijving of uw geconfigureerde online gateway',
    },
    US: {
      en: 'cash, card, bank transfer, cheque, or your configured online gateway (Stripe/PayPal)',
      de: 'Bar, Karte, Banküberweisung, Scheck oder Ihr konfiguriertes Online-Gateway (Stripe/PayPal)',
      fr: 'espèces, carte, virement bancaire, chèque ou votre passerelle en ligne configurée (Stripe/PayPal)',
      nl: 'contant, kaart, bankoverschrijving, cheque of uw geconfigureerde online gateway (Stripe/PayPal)',
    },
    GB: {
      en: 'cash, card, bank transfer, cheque, or your configured online gateway (Stripe/PayPal)',
      de: 'Bar, Karte, Banküberweisung, Scheck oder Ihr konfiguriertes Online-Gateway (Stripe/PayPal)',
      fr: 'espèces, carte, virement bancaire, chèque ou votre passerelle en ligne configurée (Stripe/PayPal)',
      nl: 'contant, kaart, bankoverschrijving, cheque of uw geconfigureerde online gateway (Stripe/PayPal)',
    },
    AE: {
      en: 'cash, card, bank transfer, or your configured online gateway (Stripe/PayPal)',
      de: 'Bar, Karte, Banküberweisung oder Ihr konfiguriertes Online-Gateway (Stripe/PayPal)',
      fr: 'espèces, carte, virement bancaire ou votre passerelle en ligne configurée (Stripe/PayPal)',
      nl: 'contant, kaart, bankoverschrijving of uw geconfigureerde online gateway (Stripe/PayPal)',
    },
  };
  const euMethods = {
    en: 'cash, card, bank transfer (IBAN/SEPA), or your configured online gateway (Stripe/PayPal/Mollie)',
    de: 'Bar, Karte, Banküberweisung (IBAN/SEPA) oder Ihr konfiguriertes Online-Gateway (Stripe/PayPal/Mollie)',
    fr: 'espèces, carte, virement bancaire (IBAN/SEPA) ou votre passerelle en ligne configurée (Stripe/PayPal/Mollie)',
    nl: 'contant, kaart, bankoverschrijving (IBAN/SEPA) of uw geconfigureerde online gateway (Stripe/PayPal/Mollie)',
  };
  const m = isEu(cc) ? euMethods : (methods[cc] || methods.IN);
  return {
    en: `Each student has a prepaid wallet. You top up the wallet (${m.en}). When sessions are completed, fees are debited automatically. Students can view balance and history in Finances → My Wallet. This is a prepaid ledger — not traditional post-paid invoicing.`,
    de: `Jeder Schüler hat ein Prepaid-Wallet. Sie laden es auf (${m.de}). Bei abgeschlossenen Sitzungen werden Gebühren automatisch abgebucht. Schüler sehen Guthaben und Historie unter Finanzen → Mein Wallet. Dies ist ein Prepaid-Konto — keine klassische Nachzahlungsrechnung.`,
    fr: `Chaque élève a un portefeuille prépayé. Vous le rechargez (${m.fr}). À la fin des séances, les frais sont débités automatiquement. Les élèves voient solde et historique sous Finances → Mon portefeuille. C'est un registre prépayé — pas une facturation post-payée classique.`,
    nl: `Elke leerling heeft een prepaid wallet. U waardeert de wallet op (${m.nl}). Wanneer sessies zijn voltooid, worden kosten automatisch afgeschreven. Leerlingen kunnen saldo en historie bekijken in Financiën → Mijn wallet. Dit is een prepaid-grootboek — geen traditionele facturatie achteraf.`,
  };
}

function manualTopupAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  const modes = {
    IN: {
      en: 'UPI, cash, bank transfer, cheque, etc.',
      de: 'UPI, Bar, Banküberweisung, Scheck usw.',
      fr: 'UPI, espèces, virement bancaire, chèque, etc.',
      nl: 'UPI, contant, bankoverschrijving, cheque, enz.',
    },
    US: {
      en: 'cash, card, bank transfer, cheque, etc.',
      de: 'Bar, Karte, Banküberweisung, Scheck usw.',
      fr: 'espèces, carte, virement bancaire, chèque, etc.',
      nl: 'contant, kaart, bankoverschrijving, cheque, enz.',
    },
    GB: {
      en: 'cash, card, bank transfer, cheque, etc.',
      de: 'Bar, Karte, Banküberweisung, Scheck usw.',
      fr: 'espèces, carte, virement bancaire, chèque, etc.',
      nl: 'contant, kaart, bankoverschrijving, cheque, enz.',
    },
    AE: {
      en: 'cash, card, bank transfer, etc.',
      de: 'Bar, Karte, Banküberweisung usw.',
      fr: 'espèces, carte, virement bancaire, etc.',
      nl: 'contant, kaart, bankoverschrijving, enz.',
    },
  };
  const euModes = {
    en: 'cash, card, SEPA / bank transfer (IBAN), etc.',
    de: 'Bar, Karte, SEPA / Banküberweisung (IBAN) usw.',
    fr: 'espèces, carte, SEPA / virement bancaire (IBAN), etc.',
    nl: 'contant, kaart, SEPA / bankoverschrijving (IBAN), enz.',
  };
  const mode = isEu(cc) ? euModes : (modes[cc] || modes.IN);
  return {
    en: `Go to Fees & Billing → Wallet Top-up. Search for the student, enter amount, choose payment mode (${mode.en}), add reference if needed, and submit.`,
    de: `Gehen Sie zu Gebühren & Abrechnung → Wallet-Aufladung. Schüler suchen, Betrag eingeben, Zahlungsart wählen (${mode.de}), Referenz hinzufügen und absenden.`,
    fr: `Allez dans Frais et facturation → Recharge portefeuille. Recherchez l'élève, saisissez le montant, choisissez le mode de paiement (${mode.fr}), ajoutez une référence si besoin et validez.`,
    nl: `Ga naar Tarieven & facturatie → Wallet opwaarderen. Zoek de leerling, voer het bedrag in, kies de betaalwijze (${mode.nl}), voeg indien nodig een referentie toe en dien in.`,
  };
}

function paymentMethodsSection(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  const section = {
    en: 'Payment Methods',
    de: 'Zahlungsmethoden',
    fr: 'Modes de paiement',
    nl: 'Betaalmethoden',
  };

  if (cc === 'IN') {
    return {
      sectionKey: 'payment-methods',
      section,
      items: [
        {
          id: 'regional.payment-methods.enable-online-payments',
          q: {
            en: 'How do I enable online payments for students?',
            de: 'Wie aktiviere ich Online-Zahlungen für Schüler?',
            fr: 'Comment activer les paiements en ligne pour les élèves ?',
            nl: 'Hoe schakel ik online betalingen voor leerlingen in?',
          },
          a: {
            en: 'Go to System → Payment Methods. Enable Razorpay, Stripe, or PayPal (as appropriate), enter your merchant API keys, and save. Razorpay is the most common choice for Indian academies.',
            de: 'Gehen Sie zu System → Zahlungsmethoden. Aktivieren Sie Razorpay, Stripe oder PayPal (je nach Bedarf), geben Sie Ihre Händler-API-Schlüssel ein und speichern Sie. Razorpay ist die häufigste Wahl für indische Akademien.',
            fr: 'Allez dans Système → Modes de paiement. Activez Razorpay, Stripe ou PayPal (selon le cas), saisissez vos clés API marchand et enregistrez. Razorpay est le choix le plus courant pour les académies indiennes.',
            nl: 'Ga naar Systeem → Betaalmethoden. Schakel Razorpay, Stripe of PayPal in (naargelang van toepassing), voer uw merchant-API-sleutels in en sla op. Razorpay is de meest gebruikelijke keuze voor Indiase academies.',
          },
        },
        {
          id: 'regional.payment-methods.student-online-methods',
          q: {
            en: 'What payment methods can students use online?',
            de: 'Welche Zahlungsmethoden können Schüler online nutzen?',
            fr: 'Quels modes de paiement les élèves peuvent-ils utiliser en ligne ?',
            nl: 'Welke betaalmethoden kunnen leerlingen online gebruiken?',
          },
          a: {
            en: 'Depends on the gateway you enable: Razorpay supports UPI, cards, and net banking; Stripe and PayPal support cards and local methods in supported currencies.',
            de: 'Abhängig vom aktivierten Gateway: Razorpay unterstützt UPI, Karten und Netbanking; Stripe und PayPal unterstützen Karten und lokale Methoden in unterstützten Währungen.',
            fr: 'Selon la passerelle activée : Razorpay prend en charge UPI, cartes et banque en ligne ; Stripe et PayPal prennent en charge cartes et méthodes locales dans les devises supportées.',
            nl: 'Afhankelijk van de gateway die u inschakelt: Razorpay ondersteunt UPI, kaarten en internetbankieren; Stripe en PayPal ondersteunen kaarten en lokale methoden in ondersteunde valuta’s.',
          },
        },
        {
          id: 'regional.payment-methods.student-pays-online',
          q: {
            en: 'What happens when a student pays online?',
            de: 'Was passiert, wenn ein Schüler online zahlt?',
            fr: 'Que se passe-t-il quand un élève paie en ligne ?',
            nl: 'Wat gebeurt er wanneer een leerling online betaalt?',
          },
          a: {
            en: 'The student enters an amount on My Wallet, completes the gateway checkout, and the amount is credited after verification.',
            de: 'Der Schüler gibt einen Betrag auf Mein Wallet ein, schließt den Gateway-Checkout ab und der Betrag wird nach Verifizierung gutgeschrieben.',
            fr: "L'élève saisit un montant sur Mon portefeuille, termine le checkout de la passerelle et le montant est crédité après vérification.",
            nl: 'De leerling voert een bedrag in op Mijn wallet, rondt de gateway-checkout af en het bedrag wordt na verificatie bijgeschreven.',
          },
        },
      ],
    };
  }

  if (isEu(cc)) {
    return {
      sectionKey: 'payment-methods',
      section,
      items: [
        {
          id: 'regional.payment-methods.enable-online-payments',
          q: {
            en: 'How do I enable online payments for students?',
            de: 'Wie aktiviere ich Online-Zahlungen für Schüler?',
            fr: 'Comment activer les paiements en ligne pour les élèves ?',
            nl: 'Hoe schakel ik online betalingen voor leerlingen in?',
          },
          a: {
            en: 'Go to System → Payment Methods. Enable Stripe, PayPal, and/or Mollie, enter your merchant API keys, and save. All three require HTTPS on your academy domain in production. Mollie is commonly used for iDEAL, Bancontact, and other European local methods.',
            de: 'Gehen Sie zu System → Zahlungsmethoden. Aktivieren Sie Stripe, PayPal und/oder Mollie, geben Sie Ihre Händler-API-Schlüssel ein und speichern Sie. Alle drei erfordern HTTPS auf Ihrer Akademie-Domain in Produktion. Mollie wird häufig für iDEAL, Bancontact und andere europäische lokale Methoden genutzt.',
            fr: 'Allez dans Système → Modes de paiement. Activez Stripe, PayPal et/ou Mollie, saisissez vos clés API marchand et enregistrez. Les trois nécessitent HTTPS sur le domaine de votre académie en production. Mollie est couramment utilisé pour iDEAL, Bancontact et autres méthodes locales européennes.',
            nl: 'Ga naar Systeem → Betaalmethoden. Schakel Stripe, PayPal en/of Mollie in, voer uw merchant-API-sleutels in en sla op. Alle drie vereisen HTTPS op uw academiedomein in productie. Mollie wordt vaak gebruikt voor iDEAL, Bancontact en andere Europese lokale methoden.',
          },
        },
        {
          id: 'regional.payment-methods.student-online-methods',
          q: {
            en: 'What payment methods can students use online?',
            de: 'Welche Zahlungsmethoden können Schüler online nutzen?',
            fr: 'Quels modes de paiement les élèves peuvent-ils utiliser en ligne ?',
            nl: 'Welke betaalmethoden kunnen leerlingen online gebruiken?',
          },
          a: {
            en: 'With Stripe, PayPal, or Mollie enabled, students can pay by card and local European methods supported by your merchant account (for example iDEAL or Bancontact via Mollie), in your academy currency from Regional Settings.',
            de: 'Mit Stripe, PayPal oder Mollie können Schüler per Karte und lokalen europäischen Methoden Ihres Händlerkontos zahlen (z. B. iDEAL oder Bancontact via Mollie), in der Akademiewährung aus den Regionaleinstellungen.',
            fr: 'Avec Stripe, PayPal ou Mollie activés, les élèves peuvent payer par carte et méthodes locales européennes supportées par votre compte marchand (par ex. iDEAL ou Bancontact via Mollie), dans la devise de l\'académie définie dans les paramètres régionaux.',
            nl: 'Met Stripe, PayPal of Mollie ingeschakeld kunnen leerlingen betalen met kaart en lokale Europese methoden die door uw merchantaccount worden ondersteund (bijvoorbeeld iDEAL of Bancontact via Mollie), in de academievaluta uit de regionale instellingen.',
          },
        },
        {
          id: 'regional.payment-methods.student-pays-online',
          q: {
            en: 'What happens when a student pays online?',
            de: 'Was passiert, wenn ein Schüler online zahlt?',
            fr: 'Que se passe-t-il quand un élève paie en ligne ?',
            nl: 'Wat gebeurt er wanneer een leerling online betaalt?',
          },
          a: {
            en: 'The student enters an amount on My Wallet, completes checkout on the gateway, and the amount is credited after return-URL confirmation and/or webhook.',
            de: 'Der Schüler gibt einen Betrag auf Mein Wallet ein, schließt den Checkout auf dem Gateway ab und der Betrag wird nach Return-URL-Bestätigung und/oder Webhook gutgeschrieben.',
            fr: "L'élève saisit un montant sur Mon portefeuille, termine le checkout sur la passerelle et le montant est crédité après confirmation d'URL de retour et/ou webhook.",
            nl: 'De leerling voert een bedrag in op Mijn wallet, rondt de checkout op de gateway af en het bedrag wordt bijgeschreven na bevestiging via de return-URL en/of webhook.',
          },
        },
      ],
    };
  }

  return {
    sectionKey: 'payment-methods',
    section,
    items: [
      {
        id: 'regional.payment-methods.enable-online-payments',
        q: {
          en: 'How do I enable online payments for students?',
          de: 'Wie aktiviere ich Online-Zahlungen für Schüler?',
          fr: 'Comment activer les paiements en ligne pour les élèves ?',
          nl: 'Hoe schakel ik online betalingen voor leerlingen in?',
        },
        a: {
          en: 'Go to System → Payment Methods. Enable Stripe and/or PayPal, enter your merchant API keys, and save. Both require HTTPS on your academy domain in production.',
          de: 'Gehen Sie zu System → Zahlungsmethoden. Aktivieren Sie Stripe und/oder PayPal, geben Sie Ihre Händler-API-Schlüssel ein und speichern Sie. Beide erfordern HTTPS auf Ihrer Akademie-Domain in Produktion.',
          fr: 'Allez dans Système → Modes de paiement. Activez Stripe et/ou PayPal, saisissez vos clés API marchand et enregistrez. Les deux nécessitent HTTPS sur le domaine de votre académie en production.',
          nl: 'Ga naar Systeem → Betaalmethoden. Schakel Stripe en/of PayPal in, voer uw merchant-API-sleutels in en sla op. Beide vereisen HTTPS op uw academiedomein in productie.',
        },
      },
      {
        id: 'regional.payment-methods.student-online-methods',
        q: {
          en: 'What payment methods can students use online?',
          de: 'Welche Zahlungsmethoden können Schüler online nutzen?',
          fr: 'Quels modes de paiement les élèves peuvent-ils utiliser en ligne ?',
          nl: 'Welke betaalmethoden kunnen leerlingen online gebruiken?',
        },
        a: {
          en: 'With Stripe or PayPal enabled, students can pay by card and other methods supported by your gateway account in your academy currency.',
          de: 'Mit Stripe oder PayPal können Schüler per Karte und anderen Methoden Ihres Gateway-Kontos in Ihrer Akademiewährung zahlen.',
          fr: 'Avec Stripe ou PayPal activés, les élèves peuvent payer par carte et autres méthodes supportées par votre compte passerelle dans la devise de l\'académie.',
          nl: 'Met Stripe of PayPal ingeschakeld kunnen leerlingen betalen met kaart en andere methoden die door uw gatewayaccount worden ondersteund in de valuta van uw academie.',
        },
      },
      {
        id: 'regional.payment-methods.student-pays-online',
        q: {
          en: 'What happens when a student pays online?',
          de: 'Was passiert, wenn ein Schüler online zahlt?',
          fr: 'Que se passe-t-il quand un élève paie en ligne ?',
          nl: 'Wat gebeurt er wanneer een leerling online betaalt?',
        },
        a: {
          en: 'The student enters an amount on My Wallet, completes checkout on the gateway, and the amount is credited after verification.',
          de: 'Der Schüler gibt einen Betrag auf Mein Wallet ein, schließt den Checkout auf dem Gateway ab und der Betrag wird nach Verifizierung gutgeschrieben.',
          fr: "L'élève saisit un montant sur Mon portefeuille, termine le checkout sur la passerelle et le montant est crédité après vérification.",
          nl: 'De leerling voert een bedrag in op Mijn wallet, rondt de checkout op de gateway af en het bedrag wordt na verificatie bijgeschreven.',
        },
      },
    ],
  };
}

function studentWalletAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  if (cc === 'IN') {
    return {
      en: 'A prepaid balance for academy fees. Your academy adds funds, or you top up online when Razorpay/Stripe/PayPal is enabled. Session fees are deducted automatically when classes are completed.',
      de: 'Ein Prepaid-Guthaben für Akademiegebühren. Ihre Akademie fügt Guthaben hinzu, oder Sie laden online auf, wenn Razorpay/Stripe/PayPal aktiviert ist. Sitzungsgebühren werden automatisch abgebucht, wenn Unterricht abgeschlossen ist.',
      fr: 'Un solde prépayé pour les frais de l\'académie. Votre académie ajoute des fonds, ou vous rechargez en ligne lorsque Razorpay/Stripe/PayPal est activé. Les frais de séance sont déduits automatiquement lorsque les cours sont terminés.',
      nl: 'Een prepaid saldo voor academiekosten. Uw academie voegt tegoed toe, of u waardeert online op wanneer Razorpay/Stripe/PayPal is ingeschakeld. Sessiekosten worden automatisch afgeschreven wanneer lessen zijn voltooid.',
    };
  }
  if (isEu(cc)) {
    return {
      en: 'A prepaid balance for academy fees. Your academy adds funds, or you top up online when Stripe, PayPal, or Mollie is enabled. Session fees are deducted automatically when classes are completed.',
      de: 'Ein Prepaid-Guthaben für Akademiegebühren. Ihre Akademie fügt Guthaben hinzu, oder Sie laden online auf, wenn Stripe, PayPal oder Mollie aktiviert ist. Sitzungsgebühren werden automatisch abgebucht, wenn Unterricht abgeschlossen ist.',
      fr: 'Un solde prépayé pour les frais de l\'académie. Votre académie ajoute des fonds, ou vous rechargez en ligne lorsque Stripe, PayPal ou Mollie est activé. Les frais de séance sont déduits automatiquement lorsque les cours sont terminés.',
      nl: 'Een prepaid saldo voor academiekosten. Uw academie voegt tegoed toe, of u waardeert online op wanneer Stripe, PayPal of Mollie is ingeschakeld. Sessiekosten worden automatisch afgeschreven wanneer lessen zijn voltooid.',
    };
  }
  return {
    en: 'A prepaid balance for academy fees. Your academy adds funds, or you top up online when Stripe or PayPal is enabled. Session fees are deducted automatically when classes are completed.',
    de: 'Ein Prepaid-Guthaben für Akademiegebühren. Ihre Akademie fügt Guthaben hinzu, oder Sie laden online auf, wenn Stripe oder PayPal aktiviert ist. Sitzungsgebühren werden automatisch abgebucht, wenn Unterricht abgeschlossen ist.',
    fr: 'Un solde prépayé pour les frais de l\'académie. Votre académie ajoute des fonds, ou vous rechargez en ligne lorsque Stripe ou PayPal est activé. Les frais de séance sont déduits automatiquement lorsque les cours sont terminés.',
    nl: 'Een prepaid saldo voor academiekosten. Uw academie voegt tegoed toe, of u waardeert online op wanneer Stripe of PayPal is ingeschakeld. Sessiekosten worden automatisch afgeschreven wanneer lessen zijn voltooid.',
  };
}

function studentPayOnlineAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  if (cc === 'IN') {
    return {
      en: 'Finances → My Wallet → enter amount → Pay Now. Supported methods depend on what your academy enabled (Razorpay, Stripe, or PayPal).',
      de: 'Finanzen → Mein Wallet → Betrag eingeben → Jetzt bezahlen. Unterstützte Methoden hängen davon ab, was Ihre Akademie aktiviert hat (Razorpay, Stripe oder PayPal).',
      fr: 'Finances → Mon portefeuille → saisir le montant → Payer maintenant. Les méthodes supportées dépendent de ce que votre académie a activé (Razorpay, Stripe ou PayPal).',
      nl: 'Financiën → Mijn wallet → bedrag invoeren → Nu betalen. Ondersteunde methoden hangen af van wat uw academie heeft ingeschakeld (Razorpay, Stripe of PayPal).',
    };
  }
  if (isEu(cc)) {
    return {
      en: 'Finances → My Wallet → enter amount → Pay Now via Stripe, PayPal, or Mollie when your academy has enabled online payments.',
      de: 'Finanzen → Mein Wallet → Betrag eingeben → Jetzt bezahlen via Stripe, PayPal oder Mollie, wenn Ihre Akademie Online-Zahlungen aktiviert hat.',
      fr: 'Finances → Mon portefeuille → saisir le montant → Payer maintenant via Stripe, PayPal ou Mollie lorsque votre académie a activé les paiements en ligne.',
      nl: 'Financiën → Mijn wallet → bedrag invoeren → Nu betalen via Stripe, PayPal of Mollie wanneer uw academie online betalingen heeft ingeschakeld.',
    };
  }
  return {
    en: 'Finances → My Wallet → enter amount → Pay Now via Stripe or PayPal when your academy has enabled online payments.',
    de: 'Finanzen → Mein Wallet → Betrag eingeben → Jetzt bezahlen via Stripe oder PayPal, wenn Ihre Akademie Online-Zahlungen aktiviert hat.',
    fr: 'Finances → Mon portefeuille → saisir le montant → Payer maintenant via Stripe ou PayPal lorsque votre académie a activé les paiements en ligne.',
    nl: 'Financiën → Mijn wallet → bedrag invoeren → Nu betalen via Stripe of PayPal wanneer uw academie online betalingen heeft ingeschakeld.',
  };
}

function studentOfflinePayAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  if (cc === 'IN') {
    return {
      en: 'Online payments must be enabled by your academy. Pay cash/UPI to the academy and ask them to record a wallet top-up.',
      de: 'Online-Zahlungen müssen von Ihrer Akademie aktiviert werden. Zahlen Sie bar/UPI an die Akademie und bitten Sie um eine Wallet-Aufladung.',
      fr: 'Les paiements en ligne doivent être activés par votre académie. Payez en espèces/UPI à l\'académie et demandez l\'enregistrement d\'une recharge de portefeuille.',
      nl: 'Online betalingen moeten door uw academie zijn ingeschakeld. Betaal contant/UPI aan de academie en vraag hen een wallet-opwaardering te registreren.',
    };
  }
  return {
    en: 'Online payments must be enabled by your academy. Pay cash or card at the academy and ask them to record a wallet top-up.',
    de: 'Online-Zahlungen müssen von Ihrer Akademie aktiviert werden. Zahlen Sie bar oder per Karte an der Akademie und bitten Sie um eine Wallet-Aufladung.',
    fr: 'Les paiements en ligne doivent être activés par votre académie. Payez en espèces ou par carte à l\'académie et demandez l\'enregistrement d\'une recharge de portefeuille.',
    nl: 'Online betalingen moeten door uw academie zijn ingeschakeld. Betaal contant of met kaart bij de academie en vraag hen een wallet-opwaardering te registreren.',
  };
}

function studentPaymentMismatchAnswer(countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  if (cc === 'IN') {
    return {
      en: 'Wait a few minutes and refresh. If still wrong after 15 minutes, contact your academy with your UPI/bank reference.',
      de: 'Warten Sie einige Minuten und aktualisieren Sie die Seite. Wenn es nach 15 Minuten noch falsch ist, kontaktieren Sie Ihre Akademie mit Ihrer UPI/Bank-Referenz.',
      fr: 'Attendez quelques minutes et actualisez. Si c\'est encore incorrect après 15 minutes, contactez votre académie avec votre référence UPI/bancaire.',
      nl: 'Wacht enkele minuten en vernieuw. Als het na 15 minuten nog onjuist is, neem contact op met uw academie met uw UPI-/bankreferentie.',
    };
  }
  return {
    en: 'Wait a few minutes and refresh. If still wrong after 15 minutes, contact your academy with your payment reference.',
    de: 'Warten Sie einige Minuten und aktualisieren Sie die Seite. Wenn es nach 15 Minuten noch falsch ist, kontaktieren Sie Ihre Akademie mit Ihrer Zahlungsreferenz.',
    fr: 'Attendez quelques minutes et actualisez. Si c\'est encore incorrect après 15 minutes, contactez votre académie avec votre référence de paiement.',
    nl: 'Wacht enkele minuten en vernieuw. Als het na 15 minuten nog onjuist is, neem contact op met uw academie met uw betalingsreferentie.',
  };
}

const OVERRIDE_BY_ID = {
  'admin.wallet-billing.how-does-wallet-billing-work': walletBillingAnswer,
  'admin.wallet-billing.are-wallet-receipts-gst-tax-invoices': taxInvoiceAnswer,
  'student.my-wallet.are-wallet-receipts-gst-invoices': taxInvoiceAnswer,
  'admin.wallet-billing.how-do-i-manually-top-up-a-student-s-wallet': manualTopupAnswer,
  'admin.dashboard-getting-started.what-is-chessdesk': (cc) => {
    const taxNote = cc === 'IN'
      ? { en: 'GST tax-invoicing', de: 'GST-Steuerrechnung', fr: 'facturation fiscale GST', nl: 'GST-facturatie' }
      : { en: 'statutory tax-invoicing', de: 'gesetzliche Steuerrechnung', fr: 'facturation fiscale légale', nl: 'wettelijke belastingfacturatie' };
    return {
      en: `ChessDesk is an academy operations platform for chess coaching centres — manage branches, coaches, students, batches, sessions, attendance, prepaid student wallets, and reports. Billing is wallet-based: students maintain a balance that is debited when sessions are completed. It is not ${taxNote.en} software.`,
      de: `ChessDesk ist eine Betriebsplattform für Schach-Akademien — verwalten Sie Filialen, Trainer, Schüler, Gruppen, Sitzungen, Anwesenheit, Prepaid-Schüler-Wallets und Berichte. Die Abrechnung ist wallet-basiert: Schüler führen ein Guthaben, das bei abgeschlossenen Sitzungen abgebucht wird. Es ist keine ${taxNote.de}-Software.`,
      fr: `ChessDesk est une plateforme opérationnelle pour centres de coaching d'échecs — gérez succursales, entraîneurs, élèves, groupes, séances, présence, portefeuilles prépayés et rapports. La facturation est basée sur le portefeuille : les élèves maintiennent un solde débité lorsque les séances sont terminées. Ce n'est pas un logiciel de ${taxNote.fr}.`,
      nl: `ChessDesk is een platform voor de bedrijfsvoering van schaakacademies — beheer filialen, coaches, leerlingen, groepen, sessies, aanwezigheid, prepaid leerlingwallets en rapporten. Facturatie is wallet-gebaseerd: leerlingen houden een saldo aan dat wordt afgeschreven wanneer sessies zijn voltooid. Het is geen software voor ${taxNote.nl}.`,
    };
  },
  'student.my-wallet.what-is-the-wallet': studentWalletAnswer,
  'student.my-wallet.how-do-i-top-up-online': studentPayOnlineAnswer,
  'student.my-wallet.i-don-t-see-pay-now-why': studentOfflinePayAnswer,
  'student.my-wallet.i-paid-online-but-balance-did-not-update': studentPaymentMismatchAnswer,
  'parent.viewing-your-child.can-i-top-up-my-child-s-wallet': (countryCode) => {
    const cc = String(countryCode || 'IN').toUpperCase();
    if (cc === 'IN') {
      return {
        en: 'Yes, when online payments are enabled. Open Wallet for the selected child, enter an amount, and pay via your academy\'s configured gateway (Razorpay, Stripe, or PayPal).',
        de: 'Ja, wenn Online-Zahlungen aktiviert sind. Öffnen Sie Wallet für das ausgewählte Kind, geben Sie einen Betrag ein und zahlen Sie über das konfigurierte Gateway Ihrer Akademie (Razorpay, Stripe oder PayPal).',
        fr: 'Oui, lorsque les paiements en ligne sont activés. Ouvrez Portefeuille pour l\'enfant sélectionné, saisissez un montant et payez via la passerelle configurée par votre académie (Razorpay, Stripe ou PayPal).',
        nl: 'Ja, wanneer online betalingen zijn ingeschakeld. Open Wallet voor het geselecteerde kind, voer een bedrag in en betaal via de geconfigureerde gateway van uw academie (Razorpay, Stripe of PayPal).',
      };
    }
    if (isEu(cc)) {
      return {
        en: 'Yes, when online payments are enabled. Open Wallet for the selected child, enter an amount, and pay via Stripe, PayPal, or Mollie.',
        de: 'Ja, wenn Online-Zahlungen aktiviert sind. Öffnen Sie Wallet für das ausgewählte Kind, geben Sie einen Betrag ein und zahlen Sie via Stripe, PayPal oder Mollie.',
        fr: 'Oui, lorsque les paiements en ligne sont activés. Ouvrez Portefeuille pour l\'enfant sélectionné, saisissez un montant et payez via Stripe, PayPal ou Mollie.',
        nl: 'Ja, wanneer online betalingen zijn ingeschakeld. Open Wallet voor het geselecteerde kind, voer een bedrag in en betaal via Stripe, PayPal of Mollie.',
      };
    }
    return {
      en: 'Yes, when online payments are enabled. Open Wallet for the selected child, enter an amount, and pay via Stripe or PayPal.',
      de: 'Ja, wenn Online-Zahlungen aktiviert sind. Öffnen Sie Wallet für das ausgewählte Kind, geben Sie einen Betrag ein und zahlen Sie via Stripe oder PayPal.',
      fr: 'Oui, lorsque les paiements en ligne sont activés. Ouvrez Portefeuille pour l\'enfant sélectionné, saisissez un montant et payez via Stripe ou PayPal.',
      nl: 'Ja, wanneer online betalingen zijn ingeschakeld. Open Wallet voor het geselecteerde kind, voer een bedrag in en betaal via Stripe of PayPal.',
    };
  },
};

const PAYMENT_SECTION_KEYS = new Set(['payment-methods', 'payment-methods-razorpay']);

/** Replace India-specific payment FAQ section and patch known answers. */
export function regionalizeFaqItems(items, countryCode) {
  const cc = String(countryCode || 'IN').toUpperCase();
  const paymentSection = paymentMethodsSection(cc);

  let out = items
    .filter((item) => !PAYMENT_SECTION_KEYS.has(item.sectionKey))
    .map((item) => {
      const override = OVERRIDE_BY_ID[item.id];
      if (typeof override === 'function') {
        return { ...item, a: override(cc) };
      }
      if (item.id === 'admin.dashboard-getting-started.what-is-the-difference-between-admin-manager-coach-and-stude' && cc !== 'IN') {
        return {
          ...item,
          a: {
            en: 'Admin has full access — branches, billing setup, online payment gateways, branding, audit logs, and all reports. Manager handles day-to-day operations for assigned branches (batches, sessions, enrollments, attendance) but cannot change payment settings, branding, or audit logs. Coach manages their students, sessions, and attendance. Student views their own schedule, attendance, wallet, and session statement.',
            de: 'Admin hat vollen Zugang — Filialen, Abrechnungseinrichtung, Online-Zahlungsgateways, Markenauftritt, Audit-Protokolle und alle Berichte. Manager führt Tagesgeschäft für zugewiesene Filialen (Gruppen, Sitzungen, Einschreibungen, Anwesenheit), kann aber Zahlungseinstellungen, Markenauftritt oder Audit-Protokolle nicht ändern. Trainer verwaltet Schüler, Sitzungen und Anwesenheit. Schüler sieht eigenen Stundenplan, Anwesenheit, Wallet und Sitzungsabrechnung.',
            fr: "L'admin a un accès complet — succursales, configuration de facturation, passerelles de paiement en ligne, image de marque, journaux d'audit et tous les rapports. Le responsable gère les opérations quotidiennes des succursales assignées (groupes, séances, inscriptions, présence) mais ne peut pas modifier paiements, image de marque ou journaux d'audit. L'entraîneur gère ses élèves, séances et présence. L'élève voit son propre planning, présence, portefeuille et relevé de séances.",
            nl: 'Admin heeft volledige toegang — filialen, facturatie-instellingen, online betaalgateways, huisstijl, auditlogboeken en alle rapporten. Manager handelt de dagelijkse werkzaamheden af voor toegewezen filialen (groepen, sessies, inschrijvingen, aanwezigheid), maar kan betaalinstellingen, huisstijl of auditlogboeken niet wijzigen. Coach beheert de eigen leerlingen, sessies en aanwezigheid. Leerling bekijkt het eigen rooster, aanwezigheid, wallet en sessieoverzicht.',
          },
        };
      }
      return item;
    });

  const walletIdx = out.findIndex((i) => i.sectionKey === 'wallet-billing');
  const insertAt = walletIdx >= 0 ? walletIdx + 1 : out.length;
  out = [
    ...out.slice(0, insertAt),
    ...paymentSection.items.map((i) => ({
      id: i.id,
      sectionKey: paymentSection.sectionKey,
      section: paymentSection.section,
      q: i.q,
      a: i.a,
    })),
    ...out.slice(insertAt),
  ];

  return out;
}

/** Resolve localized question/answer/section strings for display. */
export function localizeFaqItem(item, lang) {
  const safeLang = lang === 'de' || lang === 'fr' || lang === 'nl' ? lang : 'en';
  return {
    ...item,
    qText: faqText(item.q, safeLang),
    aText: faqText(item.a, safeLang),
    sectionText: faqText(item.section, safeLang),
  };
}
